<template>
  <MatchingProfileDataProvider v-slot="{ profile, loading: profileLoading }">
    <MatchesDataProvider
      v-slot="{
        matches,
        loading: matchesLoading,
        failed: matchesFailed,
        matchCount
      }"
    >
      <MyMeetingsDataProvider
        tab="upcoming"
        v-slot="{
          meetings: upcomingMeetings,
          loading: meetingsLoading,
          pendingCount,
          upcomingCount
        }"
      >
        <ParticipantDashboardLayout>
          <div class="dashboard-container p-6 md:p-8 space-y-6 w-full">
            <!-- Top Header Bar -->
            <header class="flex flex-col gap-6">
              <div>
                <h1
                  class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                >
                  Dashboard
                </h1>
                <p class="text-sm text-slate-500 mt-1">
                  Here's what's happening at the event today.
                </p>
              </div>
              <NetworkingQuickNav />
            </header>

            <!-- Stats Bar Row -->
            <section
              class="stats-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            >
              <!-- AI Matches -->
              <router-link
                :to="{ name: 'ai-matches' }"
                class="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-teal-300 transition-all duration-300 flex items-center justify-between group"
              >
                <div class="space-y-1">
                  <div
                    class="text-xs font-semibold tracking-wider text-slate-400 uppercase"
                  >
                    AI Matches
                  </div>
                  <div class="text-3xl font-extrabold text-slate-900">
                    {{ matchesLoading ? '...' : matchCount }}
                  </div>
                </div>
                <div class="stat-icon-wrapper bg-teal-50 text-teal-600">
                  <i class="fas fa-sparkles text-lg text-teal-600"></i>
                </div>
              </router-link>

              <!-- Meetings -->
              <router-link
                :to="{ name: 'my-meetings' }"
                class="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-teal-300 transition-all duration-300 flex items-center justify-between"
              >
                <div class="space-y-1">
                  <div
                    class="text-xs font-semibold tracking-wider text-slate-400 uppercase"
                  >
                    Meetings
                  </div>
                  <div class="text-3xl font-extrabold text-slate-900">
                    {{ meetingsLoading ? '...' : upcomingCount + pendingCount }}
                  </div>
                </div>
                <div class="stat-icon-wrapper bg-blue-50 text-blue-500">
                  <i class="fal fa-calendar-check text-lg text-blue-600"></i>
                </div>
              </router-link>

              <!-- Sessions — full width on mobile (2-col grid), single col on desktop -->
              <router-link
                :to="{ name: 'open-sessions' }"
                class="stat-card--wide bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-teal-300 transition-all duration-300 flex items-center justify-between"
              >
                <div class="space-y-1">
                  <div
                    class="text-xs font-semibold tracking-wider text-slate-400 uppercase"
                  >
                    Browse Sessions
                  </div>
                  <div class="text-md font-bold text-teal-600">Browse</div>
                </div>
                <div class="stat-icon-wrapper bg-purple-50 text-purple-500">
                  <i class="fal fa-users text-lg text-purple-600"></i>
                </div>
              </router-link>
            </section>

            <!-- Main Content Splits Grid -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <!-- Left: Recommended Sponsors List -->
              <div class="lg:col-span-2 space-y-4">
                <div class="flex items-center justify-between">
                  <h2
                    class="text-base font-bold text-slate-900 flex items-center gap-2"
                  >
                    Recommended Sponsors
                  </h2>
                  <router-link
                    :to="{ name: 'ai-matches' }"
                    class="text-teal-600 hover:text-teal-700 font-bold text-sm flex items-center gap-1 group"
                  >
                    <span>View All</span>
                    <i
                      class="fal fa-arrow-right group-hover:translate-x-0.5 transition-transform"
                    ></i>
                  </router-link>
                </div>

                <div
                  v-if="matchesLoading"
                  class="py-12 bg-white rounded-2xl border border-slate-200 text-center text-slate-500"
                >
                  <i class="fal fa-spinner-third animate-spin mr-2"></i> Loading
                  matches...
                </div>
                <div
                  v-else-if="matchCount === 0"
                  class="py-12 bg-white rounded-2xl border border-slate-200 text-center text-slate-500 px-4"
                >
                  No recommended sponsors found yet. Refresh your matching
                  profile parameters!
                </div>
                <div v-else class="space-y-3">
                  <div
                    v-for="m in topSponsors"
                    :key="m.matchId"
                    @click="openMatch(m)"
                    class="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md hover:border-teal-200 transition-all duration-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer relative overflow-hidden group"
                  >
                    <div class="flex items-center gap-4">
                      <!-- Sponsor Logo Frame -->
                      <div
                        class="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 p-1 flex-shrink-0 flex items-center justify-center overflow-hidden"
                      >
                        <img
                          v-if="m.sponsorLogoUrl"
                          :src="m.sponsorLogoUrl"
                          :alt="m.sponsorName"
                          class="w-full h-full object-contain"
                        />
                        <div
                          v-else
                          class="w-full h-full bg-slate-800 text-white flex items-center justify-center font-bold text-base rounded-lg"
                        >
                          {{ (m.sponsorName || '?')[0].toUpperCase() }}
                        </div>
                      </div>
                      <div class="min-w-0">
                        <h4
                          class="font-bold text-slate-900 hover:text-teal-600 transition-colors truncate"
                        >
                          {{ m.sponsorName }}
                        </h4>
                        <div class="flex flex-wrap items-center gap-2 mt-1">
                          <span
                            v-if="m.sponsorBoothLabel"
                            class="text-xs text-slate-400 flex items-center gap-1"
                          >
                            <i class="fal fa-map-marker-alt"></i>
                            Booth {{ m.sponsorBoothLabel }}
                          </span>
                          <span
                            v-if="m.sponsorIndustryLabel"
                            class="text-xxs font-semibold uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-0.5 rounded"
                          >
                            {{ m.sponsorIndustryLabel }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div
                      class="flex items-center justify-between sm:justify-end gap-4"
                    >
                      <!-- Scoring Gauge Badge -->
                      <div class="text-right flex flex-col items-end">
                        <span class="text-lg font-black text-teal-600">
                          {{ displayScore(m) }}%
                        </span>
                        <span
                          class="text-xxs uppercase tracking-wider font-semibold text-slate-400"
                          >Match</span
                        >
                      </div>

                      <!-- Quick Link Utilities -->
                      <div class="flex items-center gap-1.5 quick-actions">
                        <button
                          @click.stop="bookMeeting(m)"
                          class="quick-action-btn rounded-lg bg-teal-50 text-teal-600 hover:bg-teal-100 flex items-center justify-center transition-colors"
                          title="Book Meeting"
                        >
                          <i class="fal fa-calendar-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right: Affinities & Schedule -->
              <div class="space-y-6">
                <!-- Your Schedule widget -->
                <div
                  class="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <h3 class="text-base font-bold text-slate-900">
                        {{ $t('Views.Dashboard.yourSchedule') }}
                      </h3>
                      <p
                        class="text-xxs uppercase tracking-wider font-semibold text-slate-400 mt-0.5"
                      >
                        {{ scheduleDateLabel }}
                      </p>
                    </div>
                    <router-link
                      :to="{ name: 'my-meetings' }"
                      class="text-teal-600 hover:text-teal-700 text-xs font-bold"
                    >
                      Full Schedule &rarr;
                    </router-link>
                  </div>

                  <!-- Available meeting status indicator -->
                  <div
                    class="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100"
                  >
                    <div class="flex items-center gap-2">
                      <span class="w-2.5 h-2.5 rounded-full bg-teal-500"></span>
                      <span class="text-xs font-bold text-slate-700"
                        >Available for Meetings</span
                      >
                    </div>
                  </div>

                  <!-- Meetings List timeline -->
                  <div
                    v-if="meetingsLoading"
                    class="text-center py-4 text-xs text-slate-400"
                  >
                    Loading schedule...
                  </div>
                  <div
                    v-else-if="confirmedMeetings.length === 0"
                    class="text-center py-6 border border-dashed border-slate-200 rounded-xl px-4 space-y-3"
                  >
                    <i class="fal fa-calendar-plus text-xl text-slate-300"></i>
                    <p class="text-xs text-slate-500 font-medium">
                      No confirmed meetings yet.
                    </p>
                    <button
                      @click="goToMatches"
                      class="btn-brand font-bold text-xxs px-3 py-1.5 rounded-lg transition-colors shadow-sm uppercase tracking-wider"
                    >
                      Book a Meeting
                    </button>
                  </div>
                  <div v-else class="space-y-3">
                    <div
                      v-for="meet in confirmedMeetings"
                      :key="meet.id"
                      class="flex items-start gap-3 border-l-2 border-teal-500 pl-3.5 py-0.5 relative group"
                    >
                      <!-- Timeline Node Dot -->
                      <div
                        class="absolute left-[-5px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white bg-teal-500 group-hover:scale-125 transition-transform"
                      ></div>

                      <div class="flex-1 min-w-0">
                        <h4 class="text-xs font-bold text-slate-900 truncate">
                          Meeting with {{ meet.sponsorName }}
                        </h4>
                        <div class="flex flex-col gap-0.5 mt-0.5">
                          <span
                            class="text-xxs text-slate-500 flex items-center gap-1"
                          >
                            <i class="fal fa-clock"></i>
                            {{
                              formatTime(
                                meet.requestedStartAt,
                                meet.requestedEndAt
                              )
                            }}
                          </span>
                          <span
                            v-if="meet.location"
                            class="text-xxs text-slate-400 flex items-center gap-1"
                          >
                            <i class="fal fa-map-marker-alt"></i>
                            {{ meet.location }}
                          </span>
                        </div>
                      </div>
                      <span
                        class="text-[10px] font-bold text-emerald-600 uppercase bg-emerald-50 px-1.5 py-0.5 rounded leading-none shrink-0 self-start"
                      >
                        Confirmed
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ParticipantDashboardLayout>
      </MyMeetingsDataProvider>
    </MatchesDataProvider>
  </MatchingProfileDataProvider>
</template>

<script>
import Vue from 'vue';
import BookMeetingModal from '@/components/sponsor/BookMeetingModal';
import MatchesDataProvider from '@/components/providers/MatchesDataProvider.vue';
import MatchingProfileDataProvider from '@/components/providers/MatchingProfileDataProvider.vue';
import MyMeetingsDataProvider from '@/components/providers/MyMeetingsDataProvider.vue';
import { displayScore } from '@/helpers/match-score';
import ParticipantDashboardLayout from '@/components/shared/ParticipantDashboardLayout.vue';
import NetworkingQuickNav from '@/components/aiMatching/NetworkingQuickNav.vue';
import { englishOnlyI18nMixin } from '@/mixins';

export default {
  name: 'Dashboard',
  components: {
    MatchesDataProvider,
    MatchingProfileDataProvider,
    MyMeetingsDataProvider,
    ParticipantDashboardLayout,
    NetworkingQuickNav
  },
  mixins: [englishOnlyI18nMixin],
  data() {
    return {
      targetCount: 3
    };
  },
  mounted() {
    this.$store.dispatch('MyMeetings/fetchTab', 'pending');
  },
  computed: {
    topSponsors() {
      // Return top 4 recommended matches sorted by score
      return this.$store.getters['Matches/getMatches']
        ? [...this.$store.getters['Matches/getMatches']]
            .sort((a, b) => (b.calibratedScore || 0) - (a.calibratedScore || 0))
            .slice(0, 4)
        : [];
    },
    confirmedMeetings() {
      // Find confirmed meetings
      const allMeetings =
        this.$store.getters['MyMeetings/getItemsByTab']('upcoming') || [];
      return allMeetings.filter((m) => m.status === 'Confirmed').slice(0, 3);
    },
    confirmedCount() {
      return this.confirmedMeetings.length;
    },
    scheduleDateLabel() {
      // Header context for the schedule widget. The list shows upcoming confirmed meetings, so
      // surface the next meeting's date — "Today's schedule" when that meeting is today, the
      // formatted date otherwise. Uses moment (browser-local) to stay consistent with the
      // per-meeting times rendered by formatTime in this same widget.
      if (!this.confirmedMeetings.length) {
        return this.$t('Views.Dashboard.scheduleSubtitle');
      }
      const next = Vue.moment(this.confirmedMeetings[0].requestedStartAt);
      if (next.isSame(Vue.moment(), 'day')) {
        return this.$t('Views.Dashboard.todaySchedule');
      }
      return next.format('ddd, MMM D');
    },
    goalPercentage() {
      const pct = Math.round((this.confirmedCount / this.targetCount) * 100);
      return Math.min(pct, 100);
    },
    upcomingCount() {
      return this.$store.getters['MyMeetings/upcomingCount'] || 0;
    },
    pendingCount() {
      return this.$store.getters['MyMeetings/pendingCount'] || 0;
    }
  },
  methods: {
    // Calibrated score arrives as either 0-1 (legacy) or 0-100 (current); round to an integer
    // either way so the dashboard never shows the raw double like 85.0941379215131%.
    displayScore(match) {
      // Stored 0-100 — never rescale. See helpers/match-score.
      return displayScore(match?.calibratedScore);
    },
    formatTime(startIso, endIso) {
      const start = Vue.moment(startIso).format('HH:mm');
      const end = Vue.moment(endIso).format('HH:mm');
      return `${start} – ${end}`;
    },
    goToMatches() {
      this.$router.push({ name: 'ai-matches' }).catch(() => {});
    },
    openMatch(match) {
      this.$router.push({
        name: 'ai-match-sponsor',
        params: { matchId: match.matchId }
      });
    },
    bookMeeting(match) {
      if (!match || !match.sponsorId) return;
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        BookMeetingModal,
        {
          sponsorId: match.sponsorId,
          sponsorName: match.sponsorName || '',
          boothLabel: match.sponsorBoothLabel || '',
          english: true
        },
        {
          adaptive: true,
          minWidth: 320,
          maxWidth: 600,
          width: '90%',
          height: 'auto'
        }
      );
    }
  }
};
</script>

<style scoped>
.text-xxs {
  font-size: 0.65rem;
}

/* Premium Layout Background & Spacing Guard */
.dashboard-container {
  background-color: #ffffff !important; /* white page background */
}

/* Strict Layout Grid Sizer — 2 cards per row on mobile too */
.stats-grid {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: 1.5rem !important;
}
@media (min-width: 640px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
}
@media (min-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  }
}
/* Browse Sessions spans the full row on mobile/tablet (2-col grid); on
   desktop (3-col) it returns to a single column. */
@media (max-width: 1023.98px) {
  .stat-card--wide {
    grid-column: 1 / -1 !important;
  }
}

/* Header Buttons Icons Limiters */
/* Stats Icons Size Guard */
.stat-icon-wrapper {
  width: 2.5rem !important;
  height: 2.5rem !important;
  min-width: 2.5rem !important;
  min-height: 2.5rem !important;
  max-width: 2.5rem !important;
  max-height: 2.5rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 0.75rem !important;
  flex-shrink: 0 !important;
}
.stat-icon-wrapper i,
.stat-icon-wrapper svg {
  width: 1.25rem !important;
  height: 1.25rem !important;
  min-width: 1.25rem !important;
  min-height: 1.25rem !important;
  max-width: 1.25rem !important;
  max-height: 1.25rem !important;
  font-size: 1.25rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

/* Quick Action Buttons Limiters */
.quick-actions {
  display: flex !important;
  align-items: center !important;
  gap: 0.375rem !important;
}
/* Mobile-first: 44x44 minimum tap target. Desktop (sm+) restores the original 32x32 chip. */
.quick-action-btn {
  width: 2.75rem !important;
  height: 2.75rem !important;
  min-width: 2.75rem !important;
  min-height: 2.75rem !important;
  max-width: 2.75rem !important;
  max-height: 2.75rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 0.5rem !important;
  padding: 0 !important;
  flex-shrink: 0 !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  border: none !important;
}
@media (min-width: 640px) {
  .quick-action-btn {
    width: 2rem !important;
    height: 2rem !important;
    min-width: 2rem !important;
    min-height: 2rem !important;
    max-width: 2rem !important;
    max-height: 2rem !important;
  }
}
.quick-action-btn i,
.quick-action-btn svg {
  width: 1rem !important;
  height: 1rem !important;
  min-width: 1rem !important;
  min-height: 1rem !important;
  max-width: 1rem !important;
  max-height: 1rem !important;
  font-size: 1rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

/* Timeline/Schedule Clock and Map Marker Icons */
.flex-1 min-w-0 i,
.flex-1 min-w-0 svg,
.flex-wrap i,
.flex-wrap svg,
span i,
span svg {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 0.875rem !important;
  height: 0.875rem !important;
  font-size: 0.875rem !important;
  vertical-align: middle !important;
  flex-shrink: 0 !important;
}

/* Spinner Icon Sizing */
.fa-spinner-third {
  width: 1.25rem !important;
  height: 1.25rem !important;
  min-width: 1.25rem !important;
  min-height: 1.25rem !important;
  font-size: 1.25rem !important;
  display: inline-flex !important;
}
</style>
